import { useRef, useState } from "react";
import type { useBlog } from "../../controllers/useBlog";
import type { Post } from "../../models/project";
import { PostDetailView } from "./PostDetailView";

const TOOLBAR: { label: string; before: string; after: string }[] = [
  { label: "B", before: "**", after: "**" },
  { label: "I", before: "_", after: "_" },
  { label: "H", before: "## ", after: "" },
  { label: "코드", before: "`", after: "`" },
  { label: "링크", before: "[", after: "](https://)" },
  { label: "목록", before: "- ", after: "" },
];

// A project's blog (Flow-style feed): published posts, a 'new post' editor (title + markdown
// textarea with a small formatting toolbar + tags + publish toggle), and drill-in to PostDetailView.
export function BlogView({ blog }: { blog: ReturnType<typeof useBlog> }) {
  if (blog.screen === "post" && blog.selectedPost) {
    return <PostDetailView blog={blog} />;
  }
  if (blog.screen === "editor") {
    return <Editor blog={blog} />;
  }

  const published = blog.posts.filter((p) => p.status === "PUBLISHED");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>블로그 · {published.length}</h3>
        <button className="btn primary" style={{ width: "auto" }} onClick={() => blog.openEditor(null)}>＋ 새 글</button>
      </div>

      {blog.error && <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}><b style={{ color: "var(--amber)" }}>오류:</b> {blog.error}</div>}

      {blog.loading ? (
        <div className="center">불러오는 중…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {published.map((p) => <PostRow key={p.id} post={p} onOpen={() => blog.openPost(p.id)} />)}
          {published.length === 0 && <div className="sdui-list-empty">게시된 글이 없습니다.</div>}
        </div>
      )}
    </div>
  );
}

function PostRow({ post, onOpen }: { post: Post; onOpen: () => void }) {
  return (
    <button className="screen-row" style={{ width: "100%", border: "none", cursor: "pointer", flexDirection: "column", alignItems: "stretch", gap: 4 }} onClick={onOpen}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontWeight: 600 }}>{post.title}</span>
        <span className="mono" style={{ fontSize: 12 }}>{post.publishedAt ? new Date(post.publishedAt).toLocaleDateString() : ""}</span>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {post.tags.map((t) => <span key={t} className="tag">#{t}</span>)}
      </div>
    </button>
  );
}

function Editor({ blog }: { blog: ReturnType<typeof useBlog> }) {
  const editing = blog.posts.find((p) => p.id === blog.editingPostId) ?? null;
  const [title, setTitle] = useState(editing?.title ?? "");
  const [body, setBody] = useState(editing?.body ?? "");
  const [tagsText, setTagsText] = useState(editing?.tags.join(", ") ?? "");
  const [publishNow, setPublishNow] = useState(editing?.status === "PUBLISHED");
  const [busy, setBusy] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function insert(before: string, after: string) {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + before + body.slice(start, end) + after + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + before.length, end + before.length); });
  }

  async function submit() {
    if (!title.trim() || !body.trim()) return;
    setBusy(true);
    try {
      const tags = tagsText.split(",").map((t) => t.trim()).filter(Boolean);
      if (editing) {
        await blog.savePost(editing.id, { title, body, tags });
        if (publishNow && editing.status !== "PUBLISHED") await blog.publish(editing.id);
      } else {
        await blog.addPost({ title, body, tags, publishNow });
      }
      blog.backToFeed();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <button className="btn ghost" style={{ width: "auto" }} onClick={blog.backToFeed}>← 블로그</button>
      </div>
      <div className="info-card">
        <label className="sdui-field">
          <span className="sdui-label">제목</span>
          <input className="sdui-input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="sdui-field">
          <span className="sdui-label">태그 (쉼표 구분)</span>
          <input className="sdui-input" value={tagsText} onChange={(e) => setTagsText(e.target.value)} />
        </label>
        <div className="editor-toolbar">
          {TOOLBAR.map((t) => (
            <button key={t.label} type="button" onClick={() => insert(t.before, t.after)}>{t.label}</button>
          ))}
        </div>
        <label className="sdui-field">
          <span className="sdui-label">본문 (마크다운)</span>
          <textarea ref={textareaRef} className="sdui-input" rows={16} value={body} onChange={(e) => setBody(e.target.value)} />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, margin: "8px 0 16px" }}>
          <input type="checkbox" checked={publishNow} onChange={(e) => setPublishNow(e.target.checked)} />
          바로 게시
        </label>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn ghost" style={{ width: "auto" }} onClick={blog.backToFeed}>취소</button>
          <button className="btn primary" style={{ width: "auto" }} disabled={!title.trim() || !body.trim() || busy} onClick={submit}>
            {busy ? "저장 중…" : editing ? "저장" : "작성"}
          </button>
        </div>
      </div>
    </div>
  );
}
